import React, { useMemo } from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { tokens } from '@design';
import { FONTS } from '../../../constants/config';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { absoluteEnd, getTextAlign } from '../../../utils/rtl';
import type { EntityPerformanceSortBy, SortOrder } from '../../../hooks/useEntityPerformanceSort';

interface EntityPerformanceSortProps {
  sortBy: EntityPerformanceSortBy;
  order: SortOrder;
  onSortByChange: (sortBy: EntityPerformanceSortBy) => void;
  onToggleOrder: () => void;
}

const ArrowUpIcon = ({ color, size = 16 }: { color: string; size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 19V5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M5 12L12 5L19 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ArrowDownIcon = ({ color, size = 16 }: { color: string; size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M19 12L12 19L5 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ChevronDownIcon = ({ color, size = 14 }: { color: string; size?: number }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const SORT_OPTIONS: EntityPerformanceSortBy[] = [
  'strategicGoalsAchievement',
  'performanceDeviation',
  'entityName',
];

export default function EntityPerformanceSort({
  sortBy,
  order,
  onSortByChange,
  onToggleOrder,
}: EntityPerformanceSortProps) {
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const sortLabels = useMemo<Record<EntityPerformanceSortBy, string>>(
    () => ({
      strategicGoalsAchievement: t('strategyDashboard.entityPerformanceSort.strategicGoalsAchievement'),
      performanceDeviation: t('strategyDashboard.entityPerformanceSort.performanceDeviation'),
      entityName: t('strategyDashboard.entityPerformanceSort.entityName'),
    }),
    [t],
  );

  const arrowSymbol = order === 'asc' ? '↑' : '↓';
  const orderLabel =
    order === 'asc'
      ? t('strategyDashboard.entityPerformanceSort.ascending')
      : t('strategyDashboard.entityPerformanceSort.descending');

  const themed = {
    background: colors.card,
    border: colors.cardBorder,
    text: colors.textPrimary,
    muted: colors.textMuted,
    toggleBg: isDark ? colors.backgroundAlt : tokens.colors.raw.slate['50'],
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onSortByChange(event.target.value as EntityPerformanceSortBy);
  };

  return (
    <View style={[styles.container, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
      <Text style={[styles.activeLabel, { color: themed.muted }]} numberOfLines={1}>
        {`${t('strategyDashboard.entityPerformanceSort.sortedBy')}: ${sortLabels[sortBy]} ${arrowSymbol}`}
      </Text>

      <View style={[styles.controls, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        {Platform.OS === 'web' ? (
          <View
            style={[
              styles.selectWrapper,
              { backgroundColor: themed.background, borderColor: themed.border },
            ]}
          >
            <select
              value={sortBy}
              onChange={handleSelectChange}
              aria-label={t('strategyDashboard.entityPerformanceSort.sortedBy')}
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'transparent',
                color: themed.text,
                fontFamily: FONTS.regular,
                fontSize: 14,
                border: 'none',
                outline: 'none',
                paddingInlineStart: 12,
                paddingInlineEnd: 32,
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: getTextAlign(isRTL),
              } as React.CSSProperties}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {sortLabels[option]}
                </option>
              ))}
            </select>
            <View
              pointerEvents="none"
              style={[styles.selectChevron, absoluteEnd(0, isRTL)]}
            >
              <ChevronDownIcon color={themed.muted} size={14} />
            </View>
          </View>
        ) : (
          <View
            style={[
              styles.nativeFallback,
              { backgroundColor: themed.background, borderColor: themed.border },
            ]}
          >
            {SORT_OPTIONS.map((option) => {
              const active = option === sortBy;
              return (
                <TouchableOpacity
                  key={option}
                  onPress={() => onSortByChange(option)}
                  style={[
                    styles.nativeOption,
                    active && { backgroundColor: tokens.colors.semantic.general['Main Color'] },
                  ]}
                >
                  <Text
                    style={[
                      styles.nativeOptionText,
                      { color: active ? tokens.colors.raw.white : themed.text },
                    ]}
                  >
                    {sortLabels[option]}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}

        <TouchableOpacity
          onPress={onToggleOrder}
          accessibilityRole="button"
          accessibilityLabel={orderLabel}
          style={[styles.toggleButton, { backgroundColor: themed.toggleBg, borderColor: themed.border }]}
          activeOpacity={0.7}
        >
          {order === 'asc' ? (
            <ArrowUpIcon color={themed.text} />
          ) : (
            <ArrowDownIcon color={themed.text} />
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute['3'],
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute['4'],
  },
  activeLabel: {
    fontSize: tokens.typography.paragraph['paragraph caption font size'],
    fontFamily: FONTS.regular,
  },
  controls: {
    alignItems: 'center',
    gap: tokens.spacing.absolute['2'],
  },
  selectWrapper: {
    height: 36,
    minWidth: 220,
    borderRadius: tokens.radii.absolute['radius-8'],
    borderWidth: tokens.spacing.absolute['px'],
    justifyContent: 'center',
    overflow: 'hidden',
  },
  selectChevron: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nativeFallback: {
    flexDirection: 'row',
    borderRadius: tokens.radii.absolute['radius-8'],
    borderWidth: tokens.spacing.absolute['px'],
    overflow: 'hidden',
  },
  nativeOption: {
    paddingHorizontal: tokens.spacing.absolute['3'],
    paddingVertical: tokens.spacing.absolute['2'],
  },
  nativeOptionText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
  },
  toggleButton: {
    width: 36,
    height: 36,
    borderRadius: tokens.radii.absolute['radius-8'],
    borderWidth: tokens.spacing.absolute['px'],
    alignItems: 'center',
    justifyContent: 'center',
  },
});
